import { FC } from 'react';
import { Box, Text, Flex, IconButton, Spacer } from 'src/components/ui';
import { EditIcon, CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { ICustomerProfile } from 'src/lib/schemas';

interface CustomerProfileCardProps {
	customerProfile: ICustomerProfile;
	onClick?: (customerProfile: ICustomerProfile) => void;
	isSelected?: boolean;
	onEdit?: (customerProfile: ICustomerProfile) => void;
	onRemove?: (customerProfile: ICustomerProfile) => void;
	onDelete?: (customerProfile: ICustomerProfile) => void;
	showActions?: boolean;
	// Controls which action to show: 'remove' shows X icon, 'delete' shows trash icon
	actionType?: 'remove' | 'delete';
}

const CustomerProfileCard: FC<CustomerProfileCardProps> = ({
	customerProfile,
	onClick,
	isSelected = false,
	onEdit,
	onRemove,
	onDelete,
	showActions = false,
	actionType = 'remove',
}) => {
	const handleClick = () => {
		if (onClick) {
			onClick(customerProfile);
		}
	};

	const handleEdit = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onEdit) {
			onEdit(customerProfile);
		}
	};

	const handleRemove = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onRemove) {
			onRemove(customerProfile);
		}
	};

	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onDelete) {
			onDelete(customerProfile);
		}
	};

	const boxProps = onClick ? { cursor: 'pointer' } : {};

	return (
		<Box
			w="100%"
			p={4}
			borderRadius="lg"
			bg="white"
			boxShadow="0px 1px 6px 0px rgba(0, 0, 0, 0.15)"
			border={isSelected ? '2px solid #F7480B' : '2px solid transparent'}
			transition="all 0.2s ease-in-out"
			_hover={
				onClick
					? {
							boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.2)',
							transform: 'translateY(-1px)',
						}
					: {}
			}
			onClick={handleClick}
			{...boxProps}
		>
			{/* Header with title and actions */}
			<Flex alignItems="flex-start" mb={2}>
				<Text
					fontSize="lg"
					fontWeight="semibold"
					color="gray.900"
					flex="1"
					noOfLines={1}
				>
					{customerProfile.name}
				</Text>
				{showActions && (
					<Flex gap={1} ml={2}>
						{onEdit && (
							<IconButton
								aria-label="Edit customer profile"
								icon={<EditIcon />}
								size="xs"
								variant="ghost"
								colorScheme="blue"
								onClick={handleEdit}
							/>
						)}
						{actionType === 'remove' && onRemove ? (
							<IconButton
								aria-label="Remove customer profile from campaign"
								icon={<CloseIcon />}
								size="xs"
								variant="ghost"
								colorScheme="gray"
								onClick={handleRemove}
							/>
						) : null}
						{actionType === 'delete' && onDelete ? (
							<IconButton
								aria-label="Delete customer profile permanently"
								icon={<DeleteIcon />}
								size="xs"
								variant="ghost"
								colorScheme="red"
								onClick={handleDelete}
							/>
						) : null}
					</Flex>
				)}
			</Flex>

			{/* Description */}
			<Text
				color="gray.600"
				fontSize="sm"
				lineHeight="1.4"
				noOfLines={2}
				style={{
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				}}
			>
				{customerProfile.description}
			</Text>
		</Box>
	);
};

export default CustomerProfileCard;
